/**
 * CorridorAnimated - Theatrical Price Position Visualization
 *
 * Open-ended corridor bar with animated reveal:
 * - Bar zones grow from 0% to final width
 * - Dot starts at floor and slides to position
 * - Flag tag fades in after dot arrives
 * - No right edge, no ceiling — fades to transparent
 *
 * V2: Accepts actual ceiling, medium, and dimensions for contextual labels.
 */

import { useState, useEffect } from 'react';
import { STEPPER_EASE, STEPPER_MS } from '@/styles/motion';
import { T } from '@/styles/tokens';

// Design tokens
const NAVY = '#1B2A4A';
const TEAL = T.primary;

interface CorridorAnimatedProps {
  price: number;   // in cents
  floor: number;   // in cents
  ceiling?: number; // in cents — actual ceiling from calculations
  medium?: string;  // e.g. "Oil"
  width?: number;   // inches
  height?: number;  // inches
  delay?: number;   // ms before animation starts
}

export function CorridorAnimated({
  price,
  floor,
  ceiling,
  medium,
  width: pieceWidth,
  height: pieceHeight,
  delay = 2600,
}: CorridorAnimatedProps) {
  const [filled, setFilled] = useState(false);
  const [visible, setVisible] = useState(false);
  // After theatrical reveal completes, switch to STEPPER_SPRING for prop-driven changes
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setVisible(true), delay);
    const t2 = setTimeout(() => setFilled(true), delay + 100);
    // Theatrical transitions: longest is dot slide (1s + 0.3s delay) + flag fade (0.5s + 0.6s delay)
    // Allow ~1.8s total after fill starts before switching to spring timing
    const t3 = setTimeout(() => setRevealed(true), delay + 100 + 1800);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [delay]);

  // CSS transition strings — theatrical reveal vs STEPPER_SPRING equivalent
  const positionTransition = revealed
    ? `left ${STEPPER_MS}ms ${STEPPER_EASE}`
    : `left 1s ${T.easeEntrance} 0.3s`;
  const widthTransition = revealed
    ? `width ${STEPPER_MS}ms ${STEPPER_EASE}`
    : `width 0.8s ${T.easeEntrance}`;

  // Convert cents to dollars for display
  const priceDollars = Math.round(price / 100);
  const floorDollars = Math.round(floor / 100);
  const ceilingDollars = ceiling ? Math.round(ceiling / 100) : floorDollars * 2.5;

  // Calculate positions
  const rangeMin = priceDollars < floorDollars ? priceDollars * 0.85 : floorDollars * 0.85;
  const rangeMax = Math.max(ceilingDollars, priceDollars * 1.3);
  const rangeSpan = rangeMax - rangeMin;
  const floorPct = ((floorDollars - rangeMin) / rangeSpan) * 100;
  const pricePct = ((priceDollars - rangeMin) / rangeSpan) * 100;

  const isBelowFloor = priceDollars < floorDollars;
  const isOutlier = pricePct > 65;
  const markerColor = isBelowFloor ? '#EF4444' : isOutlier ? '#AF510E' : TEAL;

  // Market density annotation — show when price is in the safe zone
  const showDensityAnnotation = medium && pricePct >= floorPct && pricePct < 65;

  return (
    <div
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(8px)',
        transition: `all 0.6s ${T.easeEntrance}`,
      }}
    >
      {/* === FLAG TAG === */}
      <div style={{ position: 'relative', height: 38, marginBottom: 0 }}>
        <div
          style={{
            position: 'absolute',
            left: `${pricePct}%`,
            transform: 'translateX(-8px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            opacity: filled ? 1 : 0,
            transition: revealed
              ? `opacity 0.5s ease 0.6s, left ${STEPPER_MS}ms ${STEPPER_EASE}`
              : 'opacity 0.5s ease 0.6s',
          }}
        >
          {/* Rounded-left tag */}
          <div
            style={{
              height: 28,
              padding: '0 12px 0 14px',
              borderRadius: '14px 4px 4px 14px',
              background: markerColor,
              display: 'flex',
              alignItems: 'center',
              color: 'white',
              fontSize: 13,
              fontWeight: 700,
              boxShadow: `0 2px 8px ${markerColor}33`,
              whiteSpace: 'nowrap',
              letterSpacing: -0.3,
            }}
          >
            ${priceDollars.toLocaleString()}
          </div>
          {/* Stem connecting to dot */}
          <div
            style={{
              width: 1,
              height: 8,
              backgroundColor: markerColor,
              opacity: 0.4,
              marginLeft: 8,
            }}
          />
        </div>
      </div>

      {/* === BAR === */}
      <div
        style={{
          position: 'relative',
          height: 14,
          borderRadius: '7px 0 0 7px',
          backgroundColor: 'transparent',
          overflow: 'visible',
        }}
      >
        {/* Danger zone (below floor) — animates from 0 to floorPct */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: filled ? `${floorPct}%` : '0%',
            height: '100%',
            borderRadius: '7px 0 0 7px',
            background:
              'linear-gradient(to right, rgba(239,68,68,0.22) 0%, rgba(239,68,68,0.08) 100%)',
            transition: widthTransition,
          }}
        />

        {/* Safe zone — gradient peaks at 0.28, holds through 65%, fades to transparent */}
        <div
          style={{
            position: 'absolute',
            left: `${floorPct}%`,
            top: 0,
            width: filled ? `${100 - floorPct}%` : '0%',
            height: '100%',
            background:
              'linear-gradient(to right, rgba(61,171,165,0.08) 0%, rgba(61,171,165,0.28) 6%, rgba(61,171,165,0.28) 30%, rgba(61,171,165,0.24) 50%, rgba(61,171,165,0.18) 65%, rgba(61,171,165,0.08) 82%, transparent 100%)',
            transition: revealed
              ? `width ${STEPPER_MS}ms ${STEPPER_EASE}, left ${STEPPER_MS}ms ${STEPPER_EASE}`
              : `width 1s ${T.easeEntrance} 0.2s`,
          }}
        />

        {/* Floor line */}
        <div
          style={{
            position: 'absolute',
            left: `${floorPct}%`,
            top: -4,
            bottom: -4,
            width: 1,
            backgroundColor: NAVY,
            opacity: filled ? 0.12 : 0,
            transform: 'translateX(-50%)',
            transition: revealed
              ? `opacity 200ms ease, left ${STEPPER_MS}ms ${STEPPER_EASE}`
              : 'opacity 0.5s ease 0.4s',
          }}
        />

        {/* Price dot — slides from floor to final position */}
        <div
          style={{
            position: 'absolute',
            left: filled ? `${pricePct}%` : `${floorPct}%`,
            top: '50%',
            transform: 'translate(-50%, -50%) scale(1)',
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: markerColor,
            border: '2.5px solid white',
            boxShadow: `0 0 0 1px ${markerColor}22, 0 2px 6px ${markerColor}33`,
            zIndex: 10,
            transition: `${positionTransition}, background-color 200ms ease`,
          }}
        />
      </div>

      {/* === AXIS LABELS === */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: 12,
          alignItems: 'flex-end',
        }}
      >
        <div
          style={{
            opacity: filled ? 1 : 0,
            transition: 'opacity 0.4s ease 0.6s',
          }}
        >
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              color: '#c5c0b8',
              display: 'block',
              marginBottom: 2,
            }}
          >
            Your Floor
          </span>
          <span style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>
            ${floorDollars.toLocaleString()}
          </span>
        </div>
        <span
          style={{
            fontSize: 10,
            color: T.textMuted,
            lineHeight: 1.4,
            maxWidth: '60%',
            textAlign: 'right',
            opacity: filled ? 1 : 0,
            transition: 'opacity 0.4s ease 0.8s',
          }}
        >
          {ceiling
            ? `Above $${Math.round(ceilingDollars).toLocaleString()}, fewer comparable works sell at this size and medium`
            : 'market thins out \u2192'}
        </span>
      </div>

      {/* Market density annotation */}
      {showDensityAnnotation && (
        <p
          style={{
            fontSize: 10,
            color: '#6B6F77',
            marginTop: 10,
            lineHeight: 1.4,
            opacity: filled ? 1 : 0,
            transition: 'opacity 0.4s ease 1s',
          }}
        >
          Market Density: High — Many works sell at this price point for {medium}, {pieceWidth}"{'\u00d7'}{pieceHeight}"
        </p>
      )}
    </div>
  );
}

export default CorridorAnimated;
